<template>
  <!-- Cookie Consent Banner -->
  <div v-if="showBanner" class="cookie-banner" role="banner" aria-label="Cookie consent">
    <div class="cookie-banner-content">
      <div class="cookie-banner-text">
        <p>We use cookies and similar technologies to provide essential website functions, to improve your experience, and to analyze usage. You can choose which categories you accept. Essential cookies cannot be disabled.</p>
      </div>
      <div class="cookie-banner-actions">
        <button 
          @click="acceptAll" 
          class="btn btn-accept"
          aria-label="Accept all cookies"
        >
          Accept all
        </button>
        <button 
          @click="rejectAll" 
          class="btn btn-reject"
          aria-label="Reject non-essential cookies"
        >
          Reject non-essential
        </button>
        <button 
          @click="showCustomize = true" 
          class="btn btn-customize"
          aria-label="Customize cookie preferences"
        >
          Customize
        </button>
      </div>
    </div>
  </div>

  <!-- Cookie Customization Modal -->
  <div v-if="showCustomize" class="cookie-modal-overlay" @click.self="showCustomize = false">
    <div class="cookie-modal" role="dialog" aria-labelledby="cookie-modal-title" aria-modal="true">
      <div class="cookie-modal-header">
        <h2 id="cookie-modal-title">{{ $t('cookieConsent.title') }}</h2>
        <button @click="showCustomize = false" class="btn-close" aria-label="Close dialog">×</button>
      </div>
      
      <div class="cookie-modal-body">
        <p class="cookie-description">{{ $t('cookieConsent.description') }}</p>
        
        <div class="cookie-categories">
          <!-- Necessary Cookies (Always ON) -->
          <div class="cookie-category">
            <div class="category-header">
              <div class="category-info">
                <h3>{{ $t('cookieConsent.necessary') }}</h3>
                <p>{{ $t('cookieConsent.necessaryDesc') }}</p>
              </div>
              <div class="category-toggle">
                <input 
                  type="checkbox" 
                  id="necessary" 
                  checked 
                  disabled
                  class="cookie-toggle"
                >
                <label for="necessary" class="toggle-label">
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <!-- Analytics Cookies -->
          <div class="cookie-category">
            <div class="category-header">
              <div class="category-info">
                <h3>{{ $t('cookieConsent.analytics') }}</h3>
                <p>{{ $t('cookieConsent.analyticsDesc') }}</p>
              </div>
              <div class="category-toggle">
                <input 
                  type="checkbox" 
                  id="analytics" 
                  v-model="preferences.analytics"
                  class="cookie-toggle"
                >
                <label for="analytics" class="toggle-label">
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <!-- Marketing Cookies -->
          <div class="cookie-category">
            <div class="category-header">
              <div class="category-info">
                <h3>{{ $t('cookieConsent.marketing') }}</h3>
                <p>{{ $t('cookieConsent.marketingDesc') }}</p>
              </div>
              <div class="category-toggle">
                <input 
                  type="checkbox" 
                  id="marketing" 
                  v-model="preferences.marketing"
                  class="cookie-toggle"
                >
                <label for="marketing" class="toggle-label">
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="cookie-modal-footer">
        <button @click="acceptAll" class="btn btn-accept-all">
          {{ $t('cookieConsent.acceptAll') }}
        </button>
        <button @click="rejectAll" class="btn btn-reject">
          {{ $t('cookieConsent.rejectAll') }}
        </button>
        <button @click="savePreferences" class="btn btn-save">
          {{ $t('cookieConsent.savePreferences') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CookieConsent',
  data() {
    return {
      showBanner: false,
      showCustomize: false,
      preferences: {
        necessary: true, // Always true
        analytics: false,
        marketing: false
      }
    }
  },
  mounted() {
    this.checkConsentStatus()
  },
  methods: {
    checkConsentStatus() {
      const consent = this.getStoredConsent()
      if (!consent || this.isConsentExpired(consent)) {
        this.showBanner = true
      } else {
        this.preferences = { ...consent.preferences }
        this.applyCookieSettings()
      }
    },
    
    getStoredConsent() {
      try {
        const stored = localStorage.getItem('cookieConsent')
        return stored ? JSON.parse(stored) : null
      } catch {
        return null
      }
    },
    
    isConsentExpired(consent) {
      const expiryTime = 365 * 24 * 60 * 60 * 1000 // 1 year
      return Date.now() - consent.timestamp > expiryTime
    },
    
    saveConsent(preferences) {
      const consent = {
        version: '1.0',
        timestamp: Date.now(),
        preferences: { ...preferences }
      }
      
      try {
        localStorage.setItem('cookieConsent', JSON.stringify(consent))
        this.logConsent(consent)
        this.applyCookieSettings()
      } catch (error) {
        console.error('Failed to save cookie consent:', error)
      }
    },
    
    logConsent(consent) {
      // Log consent for audit purposes
      try {
        const logData = {
          timestamp: consent.timestamp,
          version: consent.version,
          preferences: consent.preferences,
          userAgent: navigator.userAgent,
          url: window.location.href
        }
        
        // Send to backend for audit logging
        if (window.navigator.sendBeacon) {
          window.navigator.sendBeacon('/api/v1/consent/log', JSON.stringify(logData))
        } else {
          // Fallback for older browsers
          fetch('/api/v1/consent/log', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(logData),
            keepalive: true
          }).catch(() => {
            // Fail silently for consent logging
          })
        }
      } catch (error) {
        console.warn('Failed to log consent:', error)
      }
    },
    
    applyCookieSettings() {
      // Apply analytics settings
      if (this.preferences.analytics) {
        this.enableAnalytics()
      } else {
        this.disableAnalytics()
      }
      
      // Apply marketing settings
      if (this.preferences.marketing) {
        this.enableMarketing()
      } else {
        this.disableMarketing()
      }
      
      // Emit event for other components
      this.$emit('consent-updated', this.preferences)
    },
    
    enableAnalytics() {
      // Re-enable Matomo tracking
      if (window._paq) {
        window._paq.push(['rememberConsentGiven'])
        window._paq.push(['setConsentGiven'])
      }
    },
    
    disableAnalytics() {
      // Disable Matomo tracking
      if (window._paq) {
        window._paq.push(['forgetConsentGiven'])
        window._paq.push(['requireConsent'])
      }
    },
    
    enableMarketing() {
      // Enable marketing cookies/scripts when implemented
      console.log('Marketing cookies enabled')
    },
    
    disableMarketing() {
      // Disable marketing cookies/scripts when implemented
      console.log('Marketing cookies disabled')
    },
    
    acceptAll() {
      this.preferences = {
        necessary: true,
        analytics: true,
        marketing: true
      }
      this.saveConsent(this.preferences)
      this.closeBanner()
    },
    
    rejectAll() {
      this.preferences = {
        necessary: true,
        analytics: false,
        marketing: false
      }
      this.saveConsent(this.preferences)
      this.closeBanner()
    },
    
    savePreferences() {
      this.saveConsent(this.preferences)
      this.closeBanner()
    },
    
    closeBanner() {
      this.showBanner = false
      this.showCustomize = false
    },
    
    // Method to be called by cookie settings page
    openCustomizer() {
      const consent = this.getStoredConsent()
      if (consent) {
        this.preferences = { ...consent.preferences }
      }
      this.showCustomize = true
    }
  }
}
</script>

<style scoped>
/* Cookie Banner */
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #2c3e50;
  color: white;
  padding: 1rem;
  z-index: 9999;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.3);
}

.cookie-banner-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.cookie-banner-text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
}

.cookie-banner-text p {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
}

.cookie-banner-actions {
  display: flex;
  gap: 1rem;
  flex-shrink: 0;
}

/* Cookie Modal */
.cookie-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.cookie-modal {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.cookie-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.cookie-modal-header h2 {
  margin: 0;
  color: #1945FE;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.btn-close:hover {
  background: #f5f5f5;
  color: #333;
}

.cookie-modal-body {
  padding: 1.5rem;
}

.cookie-description {
  margin-bottom: 1.5rem;
  color: #666;
  line-height: 1.5;
}

.cookie-categories {
  space-y: 1rem;
}

.cookie-category {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.category-info {
  flex: 1;
}

.category-info h3 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1rem;
}

.category-info p {
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Toggle Switch */
.category-toggle {
  flex-shrink: 0;
}

.cookie-toggle {
  display: none;
}

.toggle-label {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  cursor: pointer;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 24px;
  transition: 0.3s;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}

.cookie-toggle:checked + .toggle-label .toggle-slider {
  background-color: #1945FE;
}

.cookie-toggle:checked + .toggle-label .toggle-slider:before {
  transform: translateX(26px);
}

.cookie-toggle:disabled + .toggle-label {
  opacity: 0.7;
  cursor: not-allowed;
}

.cookie-toggle:disabled + .toggle-label .toggle-slider {
  background-color: #28a745;
}

/* Modal Footer */
.cookie-modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #eee;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-accept, .btn-accept-all {
  background: #1945FE;
  color: white;
}

.btn-accept:hover, .btn-accept-all:hover {
  background: #153BF0;
}

.btn-customize, .btn-save {
  background: #6c757d;
  color: white;
}

.btn-customize:hover, .btn-save:hover {
  background: #5a6268;
}

.btn-reject {
  background: #dc3545;
  color: white;
}

.btn-reject:hover {
  background: #c82333;
}

/* Responsive */
@media (max-width: 768px) {
  .cookie-banner-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .cookie-banner-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .category-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .cookie-modal-footer {
    justify-content: stretch;
  }
  
  .cookie-modal-footer .btn {
    flex: 1;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .cookie-modal {
    margin: 0;
    border-radius: 0;
    height: 100vh;
    max-height: none;
  }
  
  .cookie-modal-overlay {
    padding: 0;
  }
}
</style>